import React, { useState, useEffect, useRef } from 'react';

// Props du composant InputNumber
interface InputNumberProps {
    setValue: React.Dispatch<React.SetStateAction<number>>;
    tensInitialValue: number;
    onesInitialValue: number;
    onEnter: () => void;
}

// Composant pour saisir un nombre à deux chiffres (00 à 99)
// avec deux inputs séparés pour les dizaines et les unités
// Utilisé dans tous les écrans qui demandent une saisie numérique

const InputNumber: React.FC<InputNumberProps> = ({
    setValue,
    tensInitialValue,
    onesInitialValue,
    onEnter,
}) => {
    const [tens, setTens] = useState<string>(String(tensInitialValue));
    const [ones, setOnes] = useState<string>(String(onesInitialValue));
    const tensRef = useRef<HTMLInputElement | null>(null);
    const onesInputRef = useRef<HTMLInputElement | null>(null);

    // Utilitaire pour faire varier un digit
    const cycleDigit = (current: string, delta: 1 | -1): string => {
        const n = current === '' ? 0 : Number(current);
        const next = (n + delta + 10) % 10;
        return String(next);
    };

    // Met à jour la valeur finale
    useEffect(() => {
        const t = tens === '' ? 0 : Number(tens);
        const o = ones === '' ? null : Number(ones);

        // 👉 si second digit vide → on prend juste t (1 = 1 min)
        if (o === null) {
            setValue(t);
        } else {
            setValue(t * 10 + o);
        }
    }, [tens, ones, setValue]);

    // Handlers pour les changements dans les inputs
    const handleTensChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.slice(0, 1);

        if (value === '') {
            setTens('');
            return;
        }
        if (!/^[0-9]$/.test(value)) return;

        setTens(value);
        onesInputRef.current?.focus();
    };

    const handleOnesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.slice(0, 1);

        if (value === '') {
            setOnes('');
            return;
        }
        if (!/^[0-9]$/.test(value)) return;

        setOnes(value);
    };

    const handleOnesKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace') {
            if (ones === '') {
                // Deuxième backspace: efface tens + retourne au premier
                setTens('');
                tensRef.current?.focus();
            } else {
                // Premier backspace: efface juste ones
                setOnes('');
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, field: 'tens' | 'ones') => {
        if (e.key === 'Enter') {
            onEnter?.();
            return;
        }

        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            const delta: 1 | -1 = e.key === 'ArrowUp' ? 1 : -1;

            if (field === 'tens') {
                setTens((prev) => cycleDigit(prev, delta));
            } else {
                setOnes((prev) => cycleDigit(prev, delta));
            }
        }
    };

    return (
        <>
            {/* Colonne des dizaines */}
            <div className='time-input-wrap spinner-digit'>
                <input
                    type='text'
                    inputMode='numeric'
                    pattern='[0-9]*'
                    maxLength={1}
                    value={tens}
                    onChange={handleTensChange}
                    onFocus={() => setTens('')}
                    onKeyDown={(e) => handleKeyDown(e, 'tens')}
                    ref={tensRef}
                    className='time-input'
                />
            </div>

            {/* Colonne des unités */}
            <div className='time-input-wrap spinner-digit'>
                <input
                    type='text'
                    inputMode='numeric'
                    pattern='[0-9]*'
                    maxLength={1}
                    value={ones}
                    onChange={handleOnesChange}
                    onKeyDown={(e) => {
                        handleOnesKeyDown(e);
                        handleKeyDown(e, 'ones');
                    }}
                    onFocus={() => setOnes('')}
                    ref={onesInputRef}
                    className='time-input'
                />
            </div>
        </>
    );
};

export default InputNumber;
